import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {AppConfigService} from '../../../service/app-config.service';
import {Lot} from '../../../models/lot.model';

@Injectable({
    providedIn: 'root'
})
export class NlrpFieldRecordFormerTenureService {
    constructor(
        private http: HttpClient,
        private appConfigService: AppConfigService
    ) {}

    parseFormerTenures(formerTenureViewModels: any) {
        return formerTenureViewModels
            .map(formerTenureViewModel => {
                return this.parseFormerTenure(formerTenureViewModel);
            })
            .reduce(function (acc, formerTenure) {
                const formerTenureDuplicateParentPlan = acc.filter(function (v) {
                    return v.parentPlanType === formerTenure.parentPlanType && v.parentPlanNumber === formerTenure.parentPlanNumber;
                })[0];
                if (formerTenureDuplicateParentPlan) {
                    // var newLotRanges = vm.reduceRanges(formerTenureDuplicateParentPlan.lotRanges.concat(formerTenure.lotRanges));
                    // formerTenureDuplicateParentPlan.lotRanges.length = 0;
                    // Array.prototype.push.apply(formerTenureDuplicateParentPlan.lotRanges, newLotRanges);
                } else {
                    acc.push(formerTenure);
                }
                return acc;
            }, []);
    }

    parseFormerTenure(formerTenureViewModel: any) {
        const lotRanges = this.parseRanges(
            formerTenureViewModel.lotRanges
                .filter(v => {
                    return v && v.value;
                })
                .map(v => {
                    return v.value;
                })
        );
        return {
            parentPlanType: formerTenureViewModel.parentPlanType,
            parentPlanNumber: formerTenureViewModel.parentPlanNumber,
            lotRanges: lotRanges,
            title: formerTenureViewModel.title,
            subjectLandDescription: formerTenureViewModel.subjectLandDescription
        };
    }

    parseRanges(rangeTextList: any) {
        return this.toRanges(this.parseAlphaNumbersFromRanges(rangeTextList));
    }

    parseAlphaNumbersFromRanges(rangeTextList: any) {
        return this.rangesToAlphaNumbers(
            rangeTextList.map(rangeText => {
                return this.parseRange(rangeText);
            })
        ).filter(range => {
            return range;
        });
    }

    parseRange(rangeText: any) {
        const range = rangeText.split('-');

        const rangeFrom = range[0];
        const rangeTo = range.length > 1 ? range[1] : rangeFrom;
        const numberFromMatch = rangeFrom.match(/(\d{1,6})/);
        const numberToMatch = rangeTo.match(/(\d{1,6})/);
        let numberFrom = numberFromMatch ? numberFromMatch[0] : '';
        let numberTo = numberToMatch ? numberToMatch[0] : '';

        const alphaFromMatch = rangeFrom.match(/[A-Z]{1,2}/);
        const alphaToMatch = rangeTo.match(/[A-Z]{1,2}/);
        let alphaFrom = alphaFromMatch ? alphaFromMatch[0] : '';
        let alphaTo = alphaToMatch ? alphaToMatch[0] : '';

        if (alphaFrom.localeCompare(alphaTo) > 0) {
            alphaFrom = alphaToMatch ? alphaToMatch[0] : '';
            alphaTo = alphaFromMatch ? alphaFromMatch[0] : '';
            numberFrom = numberToMatch ? numberToMatch[0] : '';
            numberTo = numberFromMatch ? numberFromMatch[0] : '';
        }

        // alpheFrom and alphaTo are the same or both ""
        if (alphaFrom === alphaTo && parseInt(numberFrom) > parseInt(numberTo)) {
            return {
                prefixFrom: alphaFrom,
                prefixTo: alphaTo,
                from: parseInt(numberTo),
                to: parseInt(numberFrom)
            };
        }

        return {
            prefixFrom: alphaFrom,
            prefixTo: alphaTo,
            from: parseInt(numberFrom),
            to: parseInt(numberTo)
        };
    }

    rangesToAlphaNumbers(ranges: any) {
        return ranges
            .reduce(function (acc, range) {
                const numberFrom = range.from;
                const numberTo = range.to;
                const prefixFrom = range.prefixFrom;
                const prefixTo = range.prefixTo;
                if (range.prefixFrom === range.prefixTo) {
                    for (var n = numberFrom; n <= numberTo; n++) {
                        if (acc.indexOf({lotNumber: n, lotPrefix: prefixFrom}) === -1) {
                            acc.push({lotNumber: n, lotPrefix: prefixTo});
                        }
                    }
                } else {
                    acc.push({lotNumber: numberFrom, lotPrefix: prefixFrom});
                    acc.push({lotNumber: numberTo, lotPrefix: prefixTo});
                }
                return acc;
            }, [])
            .sort(function (a, b) {
                return a.lotPrefix === b.lotPrefix ? a.lotNumber - b.lotNumber : a.lotPrefix.localeCompare(b.lotPrefix);
            });
    }

    toRanges(items: any, rangeValueAccessor?: any) {
        let ranges = [];
        let fromItem = undefined;
        let toItem = undefined;

        for (var i = 0; i < items.length; i++) {
            fromItem = items[i];
            toItem = fromItem;
            while (
                i + 1 < items.length &&
                (rangeValueAccessor ? rangeValueAccessor(items[i + 1]) - rangeValueAccessor(items[i]) : items[i + 1] - items[i]) === 1
            ) {
                toItem = items[i + 1];
                i++;
            }
            ranges.push({from: fromItem, to: toItem});
        }

        return ranges;
    }

    reduceRanges(ranges: any) {
        return this.toRanges(this.rangesToAlphaNumbers(ranges));
    }

    getTitleStatus(titleIdentifier: string): Observable<any> {
        const url = this.appConfigService.settings.apiGatewayUrl + '/document/findTitleStatusByTitleReference/' + titleIdentifier;
        return this.http.get(url, {responseType: 'text'});
    }

    getLotsInRange(parentPlanType: string, parentPlanNumber: number, firstParentLot: number, lastParentLot: number): Observable<Lot[]> {
        const url =
            this.appConfigService.settings.apiGatewayUrl +
            '/lots/' +
            parentPlanType +
            '/' +
            parentPlanNumber +
            '/' +
            firstParentLot +
            '/' +
            lastParentLot +
            '/custom';
        return this.http.get<Lot[]>(url);
    }

    lotsToRanges(lots: any) {
        const localityTitlesLotsMap = lots.reduce((acc, lot) => {
            const mapKey = lot.locality + (lot.titles.length ? '_' + this.sortTitleIdentifiers(lot.titles).join('_') : '');
            if (acc[mapKey] === undefined) {
                acc[mapKey] = [];
            }
            acc[mapKey].push(lot);
            return acc;
        }, []);

        const lotRangesList = Object.keys(localityTitlesLotsMap).map(k => {
            const localityLots = localityTitlesLotsMap[k];

            this.toRanges(localityLots, localityLots[0].lotNumber);

            return {
                locality: localityLots[0].locality,
                ranges: this.toRanges(localityLots, localityLots[0].lotNumber)
            };
        });

        return lotRangesList.reduce((acc, v) => {
            const tmpAcc = v.ranges.map(range => {
                return {
                    locality: v.locality,
                    title: this.sortTitleIdentifiers(range.to.titles).join(),
                    prefixFrom: range.from.lotPrefix ? range.from.lotPrefix : '',
                    prefixTo: range.to.lotPrefix ? range.to.lotPrefix : '',
                    from: range.from.lotNumber,
                    to: range.to.lotNumber
                };
            });
            return acc.concat(tmpAcc);
        }, []);
    }

    sortTitleIdentifiers(titleIdentifiers: any) {
        return titleIdentifiers.sort(function (t1, t2) {
            return t1.localeCompare(t2);
        });
    }
}
